import type { MapVariableId } from './genericMapClassification';
import { extractNumericValuesForVariable } from './genericMapClassification';

export type MapReferenceStatistic =
  | 'mean_per_tract'
  | 'median_per_tract'
  | 'population_weighted_mean'
  | 'static_reference';

export type MapReferenceDefinition = {
  statistic: MapReferenceStatistic;
  /** Static fallback when statewide values are unavailable */
  staticValue: number;
  staticLabel: string;
  year: number;
};

export const MAP_REFERENCE_DEFINITIONS: Record<MapVariableId, MapReferenceDefinition> = {
  income: {
    statistic: 'median_per_tract',
    staticValue: 95521,
    staticLabel: 'California median per tract (static ref.)',
    year: 2023,
  },
  ev_adoption: {
    statistic: 'mean_per_tract',
    staticValue: 2.1,
    staticLabel: 'California mean per tract (static ref., 2023)',
    year: 2023,
  },
  total_evs: {
    statistic: 'mean_per_tract',
    staticValue: 85,
    staticLabel: 'California mean per tract (static ref., 2023)',
    year: 2023,
  },
  total_chargers: {
    statistic: 'mean_per_tract',
    staticValue: 5.2,
    staticLabel: 'California mean per tract (static ref., 2023)',
    year: 2023,
  },
  vehicles_per_household: {
    statistic: 'mean_per_tract',
    staticValue: 2.1,
    staticLabel: 'California mean per tract (static ref., 2023)',
    year: 2023,
  },
  population_density: {
    statistic: 'mean_per_tract',
    staticValue: 2500,
    staticLabel: 'California mean per tract (static ref., 2023)',
    year: 2023,
  },
  political_lean: {
    statistic: 'population_weighted_mean',
    staticValue: 15,
    staticLabel: 'California pop.-weighted mean (static ref., 2023)',
    year: 2023,
  },
  carbon_footprint: {
    statistic: 'mean_per_tract',
    staticValue: 45.2,
    staticLabel: 'California mean per tract (static ref., 2023)',
    year: 2023,
  },
  commute_time: {
    statistic: 'mean_per_tract',
    staticValue: 28.5,
    staticLabel: 'California mean per tract (static ref., 2023)',
    year: 2023,
  },
  zero_vehicle_households: {
    statistic: 'mean_per_tract',
    staticValue: 150,
    staticLabel: 'California mean per tract (static ref., 2023)',
    year: 2023,
  },
  owner_occupied_housing: {
    statistic: 'mean_per_tract',
    staticValue: 55.3,
    staticLabel: 'California mean per tract (static ref., 2023)',
    year: 2023,
  },
  educational_attainment: {
    statistic: 'mean_per_tract',
    staticValue: 32.4,
    staticLabel: 'California mean per tract (static ref., 2023)',
    year: 2023,
  },
};

export function meanOf(values: number[]): number | null {
  if (!values.length) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

export function medianOf(values: number[]): number | null {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

export function computeReferenceFromValues(
  variableId: MapVariableId,
  values: number[]
): number | null {
  const def = MAP_REFERENCE_DEFINITIONS[variableId];
  if (!values.length) return null;
  if (def.statistic === 'median_per_tract') return medianOf(values);
  return meanOf(values);
}

export function computeReferenceFromTractRows(
  variableId: MapVariableId,
  rows: any[],
  getField: (id: MapVariableId) => string
): number | null {
  const values = extractNumericValuesForVariable(rows, variableId, getField);
  return computeReferenceFromValues(variableId, values);
}

export function resolveReferenceDisplay(
  variableId: MapVariableId,
  statewideValues: number[] | null | undefined
): { value: number; label: string } {
  const def = MAP_REFERENCE_DEFINITIONS[variableId];
  const fromState = statewideValues?.length
    ? computeReferenceFromValues(variableId, statewideValues)
    : null;
  if (fromState != null && Number.isFinite(fromState)) {
    const liveLabel = def.statistic === 'median_per_tract'
      ? 'California median per tract (2023)'
      : def.statistic === 'population_weighted_mean'
        ? 'California pop.-weighted mean (2023)'
        : 'California mean per tract (2023)';
    return { value: fromState, label: liveLabel };
  }
  return { value: def.staticValue, label: def.staticLabel };
}
